<template>
  <div class="thumbnailcontainer land:block" :class="{ hidden: !showOnMobile }">
    <div class="thumbnails">
      <div
        v-for="(spread, index) of spreads"
        :key="index"
        :class="{
          'border-gray-600': !currentPageId && index === currentSpreadIndex,
          'hover:border-gray-600': itemType === 'spread',
        }"
        class="spread border border-transparent border-solid"
      >
        <div class="flex" :ref="'spread' + index">
          <a
            @click="itemType === 'spread' ? onClickItem(index * 2) : onClickItem(index, spread.page1)"
            :class="{
              'border-gray-600': itemType === 'page' && currentPageId === spread.page1.id,
              'hover:border-gray-600': itemType === 'page',
            }"
            class="border border-transparent border-solid"
          >
            <v-lazy-image class="page1" :src="spread.page1.thumbnailUrl" :ref="'image' + (index)" :alt="spread.page1.notes" />
          </a>
          <a
            v-if="spread.page2"
            @click="itemType === 'spread' ? onClickItem(index * 2) : onClickItem(index, spread.page2)"
            :class="{
              'border-gray-600': itemType === 'page' && currentPageId === spread.page2.id,
              'hover:border-gray-600': itemType === 'page',
            }"
            class="border border-transparent border-solid"
          >
            <v-lazy-image class="page2" :src="spread.page2.thumbnailUrl" :ref="'image' + (index + 1)" :alt="spread.page2.notes" />
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import VLazyImage from 'v-lazy-image'
import debounce from 'lodash/debounce'

export default {
  props: {
    pages: Array,
    pagesPerSpread: Number,
    currentPageIndex: Number,
    // This prop is optional and indicates which pages is selected within a spread.
    // If it is supplied, the page will be highlighted instead of its spread.
    // It only makes sense to supply when the itemType is page.
    currentPageId: Number,
    onClickItem: Function,
    itemType: {
      type: String,
      default: 'spread',
      validator: val => ['spread', 'page'].indexOf(val) !== -1
    },
    // This is a pretty ghetto solution to a problem, but I can't really think of a better way given the circumstances.
    // A user emailed me saying she couldn't select a thumbnail when she tries to change the album thumbnail. It's true,
    // I had this whole thing to be hidden on phones, because I was only thinking about its use on the ViewAlbum page.
    // I've got things rigged in such a silly way, because I expect people to only use the app on a phone for the
    // purpose of viewing albums, not doing album editing. Anywho, this will solve the problem. But I will basically
    // tell her that it's not really meant for editing on a phone, so use your computer.
    showOnMobile: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    spreads() {
      if (this.pagesPerSpread === 1) {
        return this.pages.map(x => ({
          page1: x,
        }))
      } else {
        const spreadsArray = this.pages.reduce((a, v, i) => {
          if (i % 2 === 0) {
            a.push({
              page1: v,
            })
          } else {
            a[a.length - 1].page2 = v
          }
          return a
        }, [])
        return spreadsArray
      }
    },
    currentSpreadIndex() {
      return Math.floor(this.currentPageIndex / this.pagesPerSpread)
    },
  },
  methods: {
    ensureThumbnailIsVisible() {
      const ref = this.$refs['spread' + this.currentSpreadIndex]
      const realRef = ref[0]
      realRef.scrollIntoView({
        // Use 'nearest' to prevent any vertical scroll.
        block: 'nearest',
      })
    },
  },
  mounted() {
    this.ensureThumbnailIsVisible = debounce(this.ensureThumbnailIsVisible, 500)

    if (this.$route.params.pageId) {
      // Whenever an image loads, the thumbnail bar changes how wide it is and thus whether our thumbnail is shown. Ensure the thumbnail we care about is shown.
      const images = Object.keys(this.$refs).filter(x => x.includes('image')).map(x => this.$refs[x][0])
      images.forEach(ref => {
        ref.onload = () => {
          this.ensureThumbnailIsVisible()
        }
      })
    }
  },
  watch: {
    currentPageIndex() {
      this.ensureThumbnailIsVisible()
    }
  },
  components: {
    VLazyImage,
  },
}
</script>

<style lang="stylus" scoped>
.thumbnailcontainer
  padding 0 10px 10px 10px
a
  cursor pointer

.thumbnails
  display flex
  overflow-x auto

  .spread
    flex-shrink 1
    white-space nowrap
    margin-right 25px

    img
      // We use a min-width so that v-lazy-image doesn't load all the images right away. Without this, the width of the images seems to be 0 or the width of the alt text, which means that they might potentially all be visible, in which case the lazy loading is negated since they are all initially visible.
      // Further note: Using just plain width doesn't seem to be good enough.
      min-width 60px
      max-width 60px
</style>
